import React, { useState, useEffect, useRef } from 'react';

function formatTime(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

// Тач-ноутбуки отпадают: их UA не mobile, но maxTouchPoints > 0.
const IS_MOBILE = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || navigator.userAgentData?.mobile === true;

export default function VideoRecorderModal({ onClose, sendFileMessage, chatId, scheduledAt, onSent }) {
  const [stream, setStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState('');

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const previewUrlRef = useRef(null);
  const rotRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const stopStream = () => streamRef.current?.getTracks().forEach((t) => t.stop());
    const startStream = async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: { facingMode: 'user' },
        });
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        stopStream();
        streamRef.current = s;
        setStream(s);
        setError('');
      } catch (e) {
        setError('Не удалось получить доступ к камере и микрофону');
      }
    };
    navigator.mediaDevices?.getUserMedia ? startStream() : setError('Камера не поддерживается браузером');

    return () => {
      cancelled = true;
      if (recorderRef.current && recorderRef.current.state !== 'inactive') {
        recorderRef.current.stop();
      }
      stopStream();
      if (timerRef.current) clearInterval(timerRef.current);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  const startRecording = () => {
    if (!streamRef.current) return;
    rotRef.current = computeRotation();
    chunksRef.current = [];
    let recorder;
    try {
      recorder = new MediaRecorder(streamRef.current, { mimeType: 'video/webm' });
    } catch (e) {
      recorder = new MediaRecorder(streamRef.current);
    }
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      clearInterval(timerRef.current);
      setIsRecording(false);
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      chunksRef.current = [];
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setSeconds(0);
    };
    recorder.start();
    recorderRef.current = recorder;
    setIsRecording(true);
    setSeconds(0);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= 30) {
          clearInterval(timerRef.current);
          stopRecording();
          setTimeout(sendVideo, 500);
          return 30;
        }
        return s + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
  };

  const computeRotation = () => {
    const live = videoRef.current;
    if (live && live.videoWidth > 0 && live.videoHeight > 0 && live.videoWidth < live.videoHeight) {
      // Кадры уже вертикальные (iOS Safari и др.) — поворот не нужен.
      return 0;
    }
    const track = streamRef.current?.getVideoTracks?.()[0];
    const facing = track?.getSettings?.()?.facingMode || 'user';
    const angle = (((window.screen?.orientation?.angle) ?? (window.orientation || 0)) % 360 + 360) % 360;
    // Десктоп/ноутбук: веб-камера отдаёт уже вертикальные кадры — поворот не нужен.
    if (!IS_MOBILE) return 0;
    // На телефоне камера отдаёт кадры сенсора (ландшафт): вертикальные кадры получаются
    // поворотом на 90° (тыл) или 270° (фронт) относительно текущей ориентации экрана.
    let rot = (90 - angle + 360) % 360;
    if (facing === 'user') rot = (rot + 180) % 360;
    return rot;
  };

  const sendVideo = async () => {
    if (!previewUrl) return;
    try {
      const blob = await fetch(previewUrl).then((r) => r.blob());
      const file = new File([blob], `video_msg_r${rotRef.current}_${Date.now()}.webm`, { type: 'video/webm' });
      await sendFileMessage(chatId, file, 'VIDEO_MESSAGE', scheduledAt || null);
      onSent && onSent(!!scheduledAt);
      onClose();
    } catch (e) {
      setError('Не удалось отправить видео');
    }
  };

  return (
    <div className="video-recorder-overlay" onClick={() => { if (!isRecording) onClose(); }}>
      <div className="video-recorder-mini" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn video-recorder-mini-close" onClick={onClose} disabled={isRecording}>✕</button>
        <div className="video-recorder-circle">
          {error && <div className="video-recorder-error">{error}</div>}
          {!previewUrl && stream && (
            <video
              className="video-recorder-preview live"
              ref={videoRef}
              muted
              autoPlay
              playsInline
            />
          )}
          {!previewUrl && !stream && !error && (
            <div className="video-recorder-empty">Подключение к камере...</div>
          )}
          {previewUrl && (
            <video className="video-recorder-preview" src={previewUrl} playsInline />
          )}
          {isRecording && (
            <span className="video-recorder-timer-circle">
              <span className="recording-dot" />
              {formatTime(seconds)}
            </span>
          )}
        </div>
        <div className="video-recorder-mini-controls">
          {!previewUrl && stream && (
            <button
              className={`rec-record-btn ${isRecording ? 'active' : ''}`}
              onClick={isRecording ? stopRecording : startRecording}
              title={isRecording ? 'Остановить' : 'Записать'}
            >
              {isRecording ? '⏹' : '●'}
            </button>
          )}
          {previewUrl && (
            <button className="send-btn video-recorder-send" onClick={sendVideo}>➤ Отправить</button>
          )}
        </div>
      </div>
    </div>
  );
}